import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Toaster } from "sonner";
import { LogOut, ExternalLink, Languages, FileText } from "lucide-react";
import { adminMe, fetchAssets, getToken, setToken } from "@/lib/api";
import { useEdition } from "@/hooks/useEdition";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { PageEditor } from "@/components/admin/PageEditor";
import { LanguagesPanel } from "@/components/admin/LanguagesPanel";
import { LangMenu } from "@/components/LangMenu";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [lang, setLang] = useState("en");
  const [n, setN] = useState(1);
  const [view, setView] = useState("pages");
  const [assets, setAssets] = useState([]);
  const { edition, pages, mutate, isLoading } = useEdition(user ? lang : "en");

  useEffect(() => {
    if (!getToken()) { setUser(false); return; }
    adminMe().then(setUser).catch(() => { setToken(null); setUser(false); });
  }, []);
  useEffect(() => { if (user) fetchAssets().then(setAssets).catch(() => setAssets([])); }, [user]);

  if (user === null) return <div className="admin-root flex items-center justify-center reader-label">Checking session…</div>;
  if (!user) return <AdminLogin onSuccess={setUser} />;

  const page = pages.find((p) => p.n === n);
  const onSaved = (saved) => mutate((d) => d && { ...d, pages: d.pages.map((p) => (p.n === saved.n ? saved : p)) }, { revalidate: false });

  return (
    <div className="admin-root" data-testid="admin-page">
      <Toaster theme="dark" position="bottom-right" />
      <header className="flex items-center justify-between px-6 h-14" style={{ borderBottom: "1px solid rgba(217,218,221,0.12)", background: "#181818" }}>
        <div className="flex items-center gap-4">
          <div className="reader-label" style={{ color: "#fff" }}>ROOTS <span style={{ opacity: 0.5 }}>· Editor</span></div>
          <div className="lang-pill" data-testid="admin-view-switch">
            <button className={view === "pages" ? "on" : ""} onClick={() => setView("pages")} data-testid="admin-view-pages"><FileText size={11} style={{ display: "inline", marginRight: 6 }} />Pages</button>
            <button className={view === "languages" ? "on" : ""} onClick={() => setView("languages")} data-testid="admin-view-languages"><Languages size={11} style={{ display: "inline", marginRight: 6 }} />Languages</button>
          </div>
          {view === "pages" && <LangMenu lang={lang} onLang={setLang} testid="admin-lang" align="start" />}
        </div>
        <div className="flex items-center gap-3">
          <Link to={`/read/${lang}?p=${n}`} target="_blank" className="btn-silver" style={{ padding: "7px 12px" }} data-testid="admin-open-reader"><ExternalLink size={12} /> View in reader</Link>
          <span className="text-xs opacity-60">{user.email}</span>
          <button className="reader-btn" title="Sign out" onClick={() => { setToken(null); setUser(false); }} data-testid="admin-logout"><LogOut size={16} /></button>
        </div>
      </header>
      {view === "languages" ? (
        <main className="px-6" style={{ maxHeight: "calc(100vh - 56px)", overflowY: "auto" }}><LanguagesPanel /></main>
      ) : (
      <div className="grid" style={{ gridTemplateColumns: "260px 1fr", minHeight: "calc(100vh - 56px)" }}>
        <aside style={{ borderRight: "1px solid rgba(217,218,221,0.1)", background: "#161616", overflowY: "auto", maxHeight: "calc(100vh - 56px)" }} data-testid="admin-page-list">
          {pages.map((p) => (
            <button key={p.n} className={`admin-page-row ${p.n === n ? "on" : ""}`} onClick={() => setN(p.n)} data-testid={`admin-page-row-${p.n}`}>
              <span className="font-head" style={{ fontSize: 18, width: 28 }}>{String(p.n).padStart(2, "0")}</span>
              <span className="flex-1 min-w-0">
                <span className="block truncate text-sm">{p.title || p.template}</span>
                <span className="block text-[10px] uppercase tracking-widest opacity-50">{p.template}{p.edited ? " · edited" : ""}</span>
              </span>
            </button>
          ))}
        </aside>
        <main className="px-6 pt-2" style={{ maxHeight: "calc(100vh - 56px)", overflowY: "auto" }}>
          {page && edition ? <PageEditor key={`${lang}-${page.n}`} page={page} edition={edition} assets={assets} onSaved={onSaved} /> : <div className="reader-label py-10">{isLoading ? "Loading…" : "This edition has not been generated yet — see Languages."}</div>}
        </main>
      </div>
      )}
    </div>
  );
}
